/**
 * Layouts Tab Component
 * Displays template layouts in a responsive grid
 * Fetches validation results and renders TemplateLayoutCard for each slide
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { templateAPI } from '../../services/api';
import { TemplateLayoutCard, TemplateLayoutSlide } from './TemplateLayoutCard';
import { slidePathMapping } from '../../services/slideMapping';

interface LayoutsTabProps {
  conversationId: string;
}

export const LayoutsTab: React.FC<LayoutsTabProps> = ({ conversationId }) => {
  const navigate = useNavigate();
  const [layouts, setLayouts] = useState<TemplateLayoutSlide[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isExporting, setIsExporting] = useState(false);

  useEffect(() => {
    fetchLayouts();
  }, [conversationId]);

  const fetchLayouts = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const response = await templateAPI.getValidationResults(conversationId);

      if (response.success && response.validation.slides) {
        setLayouts(response.validation.slides);
      } else {
        setError('Failed to load layouts');
      }
    } catch (err) {
      console.error('Failed to fetch layouts:', err);
      if ((err as any)?.response?.status === 404) {
        setError('No validation results found. Please run validation first.');
      } else {
        setError('Failed to load layouts. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleSlideClick = async (slide: TemplateLayoutSlide) => {
    // Don't navigate if slide doesn't have GSAP JSON yet
    if (!slide.slideJsonPath) {
      console.log('Slide has no GSAP JSON yet:', slide.slideId);
      return;
    }

    try {
      // Small delay to allow UI to update
      await new Promise(resolve => requestAnimationFrame(() => resolve(undefined)));

      // Generate hash from slide path for URL-safe navigation
      const slideHash = await slidePathMapping.generateHashForPath(slide.slideJsonPath);

      // Navigate to full-screen slide preview
      navigate({
        to: "/chat/$conversationId/slide/$slideHash",
        params: { conversationId, slideHash },
        search: {
          slide: undefined,
          fromTab: 'layouts' // Preserve layouts tab for back navigation
        },
      });
    } catch (error) {
      console.error('Failed to navigate to slide:', error);
    }
  };

  const handleExportAll = async () => {
    try {
      setIsExporting(true);

      // Call export API
      const blob = await templateAPI.exportLayouts(conversationId);

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${conversationId}_layouts_${new Date().toISOString().replace(/[:.]/g, '-')}.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Clean up
      window.URL.revokeObjectURL(url);

      console.log('✅ Layout export completed successfully');
    } catch (err) {
      console.error('Failed to export layouts:', err);
      alert('Failed to export layouts. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="h-full flex items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
          <p className="text-sm text-muted-foreground">Loading layouts...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="h-full flex items-center justify-center bg-background p-8">
        <div className="max-w-md text-center">
          <svg
            className="w-16 h-16 text-red-500 mx-auto mb-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <h3 className="text-lg font-semibold text-foreground mb-2">Failed to Load Layouts</h3>
          <p className="text-sm text-muted-foreground mb-4">{error}</p>
          <button
            onClick={fetchLayouts}
            className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // Empty state
  if (layouts.length === 0) {
    return (
      <div className="h-full flex items-center justify-center bg-background p-8">
        <div className="max-w-md text-center">
          <svg
            className="w-16 h-16 text-muted-foreground mx-auto mb-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
            />
          </svg>
          <h3 className="text-lg font-semibold text-foreground mb-2">No Layouts Available</h3>
          <p className="text-sm text-muted-foreground">
            Run the validation step to generate template layouts.
          </p>
        </div>
      </div>
    );
  }

  // Grid view with layouts
  return (
    <div className="h-full flex flex-col bg-background">
      {/* Header */}
      <div className="flex-shrink-0 px-6 py-4 border-b bg-card">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-foreground">Template Layouts</h2>
            <p className="text-sm text-muted-foreground mt-1">
              Showing {layouts.length} {layouts.length === 1 ? 'layout' : 'layouts'}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleExportAll}
              disabled={isExporting || layouts.filter(l => l.hasGsapSlide).length === 0}
              className="px-3 py-1.5 text-xs font-medium bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed border border-primary rounded-md transition-colors flex items-center gap-1.5"
            >
              {isExporting ? (
                <>
                  <div className="w-3 h-3 border-2 border-primary-foreground border-t-transparent rounded-full animate-spin"></div>
                  Exporting...
                </>
              ) : (
                <>
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                    />
                  </svg>
                  Export All
                </>
              )}
            </button>
            <button
              onClick={fetchLayouts}
              className="px-3 py-1.5 text-xs font-medium text-muted-foreground hover:text-foreground border border-border rounded-md hover:bg-muted/50 transition-colors flex items-center gap-1"
            >
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                />
              </svg>
              Refresh
            </button>
          </div>
        </div>
      </div>

      {/* Grid Content */}
      <div className="flex-1 overflow-y-auto p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-5 auto-rows-auto">
          {layouts.map((layout, index) => (
            <div
              key={layout.slideId}
              className="animate-in fade-in slide-in-from-bottom-4"
              style={{
                animationDelay: `${Math.min(index * 50, 500)}ms`,
                animationFillMode: 'backwards',
              }}
            >
              <TemplateLayoutCard
                slide={layout}
                conversationId={conversationId}
                onSlideClick={handleSlideClick}
                index={index}
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
